// Броячите за значките в менюто. Един лек GET на всяка складова страница
// (кешира се 30 s) — НЕ зареждаме чакащите отчети целите само за едно число.
// „Табло" носи сбора от значките на операциите (`attentionTotal`), за да се
// вижда какво чака и когато групата с операциите е свита. Незавършените
// листове (доставка, изписване, инвентаризация, прехвърляне) са черновите на
// ресторанта — мои и на колегите.
//
// До BE с `/inventory/nav-counts` (404) значката на „Разнасяне" пада на броя от
// `usePendingReports`, а тези на черновите — на локалната чернова в този
// браузър; BE отпреди черновите на другите листове просто няма техните броеве
// (значката мълчи). В локален режим (каса без интернет) значките мълчат:
// касата не знае за склада. `overduePendingReports` (закъснелите от чакащите)
// не е значка, а оцветява значката на „Разнасяне" в червено — тонът е в
// `navBadgeToneOf`; стар BE без полето → кехлибарено.
import { useInventoryNavCounts } from "../../../../hooks/useInventoryNavCounts";
import { usePendingReports } from "../../../../hooks/useInventory";
import { useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import { DELIVERY_DRAFT_STORE, hasDeliveryDraftContent } from "../../delivery/components/deliveryWorksheetShared";
import { STOCKTAKE_DRAFT_STORE, hasStocktakeDraftContent } from "../../stocktake/components/stocktakeDraft";
import { TRANSFER_DRAFT_STORE, hasTransferDraftContent } from "../../transfer/components/transferDraft";
import { WRITEOFF_DRAFT_STORE, hasWriteoffDraftContent } from "../../writeoff/components/writeoffDraft";
import type { LocalDraftStore } from "../drafts/draftModel";
import { isReportOverdue } from "../../posting/components/pendingReportAge";
import { operationsBadgeTotal, type NavBadgeCounts } from "./inventoryNav";

export type SidebarBadges = NavBadgeCounts;

// 1 — в този браузър има незавършена чернова от типа
const localDraftCount = <P>(store: LocalDraftStore<P>, hasContent: (payload: P) => boolean) => {
  const draft = store.read();
  return draft && hasContent(draft) ? 1 : 0;
};

// Значките на операциите + сборът им за „Табло"
export const withAttentionTotal = (badges: SidebarBadges): SidebarBadges => ({
  ...badges,
  attentionTotal: operationsBadgeTotal(badges),
});

export const useSidebarBadges = (): SidebarBadges => {
  const isLocal = useIsEdgeLocal();
  const counts = useInventoryNavCounts({ refetchInterval: 60_000 });
  const fallback = !isLocal && counts.isError;
  const pending = usePendingReports(fallback);

  if (isLocal) return {};
  if (counts.data) {
    return withAttentionTotal({
      pendingReports: counts.data.pendingReports,
      overduePendingReports: counts.data.overduePendingReports ?? 0,
      deliveryDrafts: counts.data.deliveryDrafts,
      unpaidDeliveries: counts.data.unpaidDeliveries,
      writeoffDrafts: counts.data.writeoffDrafts ?? 0,
      stocktakeDrafts: counts.data.stocktakeDrafts ?? 0,
      transferDrafts: counts.data.transferDrafts ?? 0,
    });
  }
  if (!fallback) return {};
  return withAttentionTotal({
    pendingReports: pending.data?.length ?? 0,
    overduePendingReports: (pending.data ?? []).filter((report) => isReportOverdue(report.reportDate)).length,
    deliveryDrafts: localDraftCount(DELIVERY_DRAFT_STORE, hasDeliveryDraftContent),
    writeoffDrafts: localDraftCount(WRITEOFF_DRAFT_STORE, hasWriteoffDraftContent),
    stocktakeDrafts: localDraftCount(STOCKTAKE_DRAFT_STORE, hasStocktakeDraftContent),
    transferDrafts: localDraftCount(TRANSFER_DRAFT_STORE, hasTransferDraftContent),
  });
};
